import type { ViceGovernorListComponent, ViceGovernorItem } from "../../types";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

/**
 * Vice-governors block for a governor detail page. A titled grid of the
 * vice-governors who served alongside this governor, fed from the governor
 * record's `viceGouverneurs` list field (each entry = portrait + nom +
 * optional période).
 *
 * Each card shows the vice-governor's portrait (from the record's `image`
 * field); when none is set it falls back to a neutral avatar carrying the
 * person's initials, so a card is never a broken image and the grid stays
 * even. Renders NOTHING when the list is empty — a governor with no recorded
 * vice-governors shows no empty section, no stub.
 *
 * On-palette only: primary blue `#00B862` heading, navy `#121212` names,
 * muted blue-grey `#6B7280` period line, soft slate frame matching the other
 * detail-page blocks.
 */
export function ViceGovernorListRenderer({ component }: { component: ViceGovernorListComponent }) {
  const content = component.content ?? { items: [] };
  const items = (Array.isArray(content.items) ? content.items : []).filter(
    (v) => v && typeof v.nom === "string" && v.nom.trim() !== ""
  );
  if (items.length === 0) return null;

  const title = (content.title ?? "").trim() || "Vice-gouverneurs";

  return (
    <section className="mt-8 rounded-lg border border-slate-200 bg-white p-5 shadow-[var(--shadow-e1)] sm:p-6">
      <h2 className="mb-4 text-[18px] font-semibold text-[#00B862] xl:text-[20px]">{title}</h2>
      <ul className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        {items.map((v, i) => {
          const { src } = resolveImageValue(v.image);
          const period = formatPeriod(v);
          return (
            <li key={`${v.nom}-${i}`} className="flex items-center gap-3">
              {src ? (
                <RuntimeImage
                  src={src as string}
                  alt={v.nom}
                  className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#E6F4EA] text-[15px] font-semibold text-[#00B862] ring-1 ring-slate-200"
                >
                  {initials(v.nom)}
                </span>
              )}
              <span className="flex min-w-0 flex-col">
                <span className="text-[15px] font-medium text-[#121212]">{v.nom}</span>
                {period ? <span className="text-[13px] text-[#6B7280]">{period}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Up to two initials from the name, for the no-portrait fallback avatar. */
function initials(nom: string): string {
  const parts = nom.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return (first + last).toUpperCase();
}

/** "1997 – 2013" / "depuis 1997" / "" — tolerant of free-text dates or years. */
function formatPeriod(v: ViceGovernorItem): string {
  const start = (v.dateDebut ?? "").toString().trim();
  const end = (v.dateFin ?? "").toString().trim();
  if (start && end) return `${start} – ${end}`;
  if (start && !end) return `depuis ${start}`;
  if (!start && end) return `jusqu'en ${end}`;
  return "";
}
